import { useCallback, useMemo, type FC, type MouseEvent } from 'react';
import { Link, type LinkProps, useNavigate } from 'react-router-dom';

import type { ArtistLike } from '../../utils/resolveArtistRoute';
import { extractSpotifyArtistId, resolveArtistRoute } from '../../utils/resolveArtistRoute';
import { ensureComposerKnown, getCatalogIdForSpotifyId } from '../../utils/composerRegistry';
import { useComposerIdSet } from '../../utils/useComposerIdSet';

type ArtistInput = ArtistLike | string | null | undefined;

const isModifiedClick = (event: MouseEvent<HTMLAnchorElement>) =>
  event.metaKey || event.altKey || event.ctrlKey || event.shiftKey || event.button !== 0;

interface ComposerAwareLinkProps extends Omit<LinkProps, 'to'> {
  artist: ArtistInput;
  composerIds?: ReadonlySet<string>;
}

export const ComposerAwareLink: FC<ComposerAwareLinkProps> = (props) => {
  const { artist, children, composerIds, onClick, target, ...linkProps } = props;
  const navigate = useNavigate();
  const fallbackComposerIds = useComposerIdSet();

  const effectiveComposerIds = composerIds ?? fallbackComposerIds;
  const destination = useMemo(
    () => resolveArtistRoute(artist ?? undefined, effectiveComposerIds),
    [artist, effectiveComposerIds]
  );

  const handleClick = useCallback(
    async (event: MouseEvent<HTMLAnchorElement>) => {
      onClick?.(event);
      if (event.defaultPrevented) return;
      if (isModifiedClick(event)) return;

      const spotifyArtistId = extractSpotifyArtistId(artist ?? undefined);
      if (!spotifyArtistId) return;

      if (effectiveComposerIds?.has(spotifyArtistId)) {
        return;
      }

      if (destination.startsWith('/composer/')) {
        return;
      }

      const isComposer = await ensureComposerKnown(spotifyArtistId);
      if (!isComposer) return;

      event.preventDefault();
      event.stopPropagation();

      const catalogId = getCatalogIdForSpotifyId(spotifyArtistId) ?? spotifyArtistId;
      const composerPath = `/composer/${catalogId}`;

      if (target === '_blank') {
        window.open(composerPath, '_blank', 'noopener');
        return;
      }

      navigate(composerPath);
    },
    [artist, destination, effectiveComposerIds, navigate, onClick, target]
  );

  return (
    <Link {...linkProps} target={target} to={destination} onClick={handleClick}>
      {children}
    </Link>
  );
};

export default ComposerAwareLink;
